import {
  hasStartEventInput,
  type AssigneeBuckets,
  type AssigneeModel,
  type EntityOrSuggestionRef,
  type GetProcessDraftResponse,
  type ProcessDraftAssignment
} from '@bliks-io/contracts';
import type { SuccessorCollectionKey } from '@bliks-io/ui';
import { SUCCESSOR_MODEL_BY_TYPE } from '@/lib/successorEntityMapping';
import type { SuccessorUpdateData } from '../types';

type StartAssignment = Pick<ProcessDraftAssignment, 'assignedTo' | 'assignedToModel'>;

const BUCKETS = Object.keys(SUCCESSOR_MODEL_BY_TYPE) as SuccessorCollectionKey[];

const BUCKET_BY_MODEL = Object.fromEntries(
  BUCKETS.map((bucket) => [SUCCESSOR_MODEL_BY_TYPE[bucket], bucket])
) as Record<AssigneeModel, SuccessorCollectionKey>;

// persons / externalContacts travel as plain entity ids.
const toRef = (entry: string | EntityOrSuggestionRef): EntityOrSuggestionRef =>
  typeof entry === 'string' ? { type: 'entity', id: entry } : entry;

const refKey = (ref: EntityOrSuggestionRef): string => `${ref.type}:${ref.id}`;

/**
 * The draft's start row, the assignments attached to it (the card's value —
 * the same `hasStartEventInput` call publish's invite dispatch uses), and
 * whether any node counts as start-attached under the publish rule
 * (`validate-draft-for-publish.ts` `hasStartInput`, which also accepts a step's legacy
 * start anchors).
 */
export function findStartAttachment(
  draft: Pick<GetProcessDraftResponse, 'events' | 'draftAssignments' | 'steps'> | null
) {
  const startEvent = draft?.events?.find((event) => event.eventKind === 'start');
  if (!draft || !startEvent) {
    return { startEvent, startId: undefined, startAssignments: [], hasStartAttachedNode: false };
  }
  const startId = String(startEvent.stableStepId);
  const startIds = new Set([startId]);
  const startAssignments = draft.draftAssignments.filter((a) =>
    hasStartEventInput(a.inputs, startIds)
  );
  const hasStartAttachedNode =
    startAssignments.length > 0 ||
    draft.steps.some((step) =>
      hasStartEventInput(step.inputs, startIds, {
        previousStep: step.previousStep,
        sourceTags: true
      })
    );
  return { startEvent, startId, startAssignments, hasStartAttachedNode };
}

/** The starter card's value: each assignee of the start-attached assignments, once. */
export function startAssigneeBuckets(
  assignments: readonly StartAssignment[]
): Required<SuccessorUpdateData> {
  const value: Required<SuccessorUpdateData> = {
    persons: [],
    orgFunctions: [],
    systems: [],
    externalContacts: [],
    successorStatus: ''
  };
  const seen = new Set<string>();
  for (const { assignedTo, assignedToModel } of assignments) {
    if (!assignedTo || !assignedToModel) continue;
    const bucket = BUCKET_BY_MODEL[assignedToModel];
    const key = `${bucket}:${refKey(assignedTo)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (bucket === 'orgFunctions' || bucket === 'systems') {
      value[bucket].push(assignedTo);
    } else if (assignedTo.type === 'entity') {
      value[bucket].push(assignedTo.id);
    }
  }
  return value;
}

function diffEntries<T extends string | EntityOrSuggestionRef>(
  from: T[] | undefined,
  against: T[] | undefined
): T[] | undefined {
  if (!from || !against) return undefined;
  const againstKeys = new Set(against.map((entry) => refKey(toRef(entry))));
  const missing = from.filter((entry) => !againstKeys.has(refKey(toRef(entry))));
  return missing.length > 0 ? missing : undefined;
}

function bucketsOrNull(buckets: AssigneeBuckets): AssigneeBuckets | null {
  return BUCKETS.some((bucket) => buckets[bucket] !== undefined) ? buckets : null;
}

/**
 * Diff the card's emitted per-bucket lists against the rendered value: new
 * refs are the additions, dropped refs the removals. A bucket missing from
 * the update is left alone.
 */
export function planStartAssigneeChange(
  update: SuccessorUpdateData,
  current: Required<SuccessorUpdateData>
): { additions: AssigneeBuckets | null; removals: AssigneeBuckets | null } {
  return {
    additions: bucketsOrNull({
      persons: diffEntries(update.persons, current.persons),
      orgFunctions: diffEntries(update.orgFunctions, current.orgFunctions),
      systems: diffEntries(update.systems, current.systems),
      externalContacts: diffEntries(update.externalContacts, current.externalContacts)
    }),
    removals: bucketsOrNull({
      persons: diffEntries(current.persons, update.persons),
      orgFunctions: diffEntries(current.orgFunctions, update.orgFunctions),
      systems: diffEntries(current.systems, update.systems),
      externalContacts: diffEntries(current.externalContacts, update.externalContacts)
    })
  };
}
